import { faCreditCard } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useState } from 'react';
import LoadingScreen from '../../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelLink from '../../../components/voxel/Link/VoxelLink';
import VoxelPanel from '../../../components/voxel/Surfaces/VoxelPanel';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { AppTierTypeGq } from '../../../graphql/projects/models/AppTierTypeGq';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { SubscriptionEventGq } from '../../../graphql/users/models/SubscriptionEventGq';
import { UserTeamRoleGq } from '../../../graphql/users/models/UserTeamRoleGq';
import { usersApiService } from '../../../graphql/users/usersApiService';
import { TeamMenuChoices } from '../TeamPage';
import LicenseSettings from './LicenseSettings';
import SetupSubscriptionButton from './SetupSubscriptionButton';
import SubscriptionEvents from './SubscriptionEvents';

export interface SubscriptionPageProps {
  team: CurrentTeamGq;
  user: CurrentUserGq;
  subscriptionEvents: SubscriptionEventGq[];
  reload: () => Promise<unknown>;
}

export const SubscriptionPage: React.FC<SubscriptionPageProps> = ({
  team,
  user,
  reload,
  subscriptionEvents,
}) => {
  // Portal
  const stripeCreatePortalSession = usersApiService.mutations.createStripeCustomerPortalSession();
  const [creatingPortalSession, setCreatingPortalSession] = useState(false);
  const handleCreatePortalSession = (): Promise<unknown> => {
    setCreatingPortalSession(true);
    return stripeCreatePortalSession({})
      .then((result) => {
        window.location.href = result;
      })
      .catch((error) => {
        console.error(error);
        setCreatingPortalSession(false);
      });
  };

  if (!team) {
    return <LoadingScreen />;
  }

  if (team.voxelTeam) {
    return (
      <div>
        <VoxelText>Subscription is disabled for the support team</VoxelText>
      </div>
    );
  }

  if (!team?.accessUserCanEditBilling) {
    return (
      <div>
        <VoxelAlert title="Ask team owner">
          You don't have sufficient permissions to edit the subscription. Your team owners can edit
          this. <VoxelLink href={`/team/${TeamMenuChoices.Members}`}>See members section</VoxelLink>{' '}
        </VoxelAlert>
      </div>
    );
  }

  const showPayment =
    user.teamRole === UserTeamRoleGq.OWNER &&
    ![AppTierTypeGq.VOXEL_SUPPORT, AppTierTypeGq.CUSTOM].includes(user.team.appTier.tierType);

  const isPaid = [AppTierTypeGq.CUSTOM, AppTierTypeGq.BASIC].includes(team.appTier.tierType);

  const subscriptionStartsLater =
    !!team.subscriptionStartsAt && moment.utc(team.subscriptionStartsAt).isBefore();
  const trialExpires = !team.subscriptionStartsAt && !!team.trialExpiresAt;
  const trialExpired =
    !team.subscriptionStartsAt &&
    !!team.trialExpiresAt &&
    moment.utc(team.trialExpiresAt).isBefore();

  return (
    <>
      {/* View */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <VoxelPanel paddingClass="p-6">
            <div className="pb-4">
              {team.appTier.tierType !== AppTierTypeGq.NONE && (
                <VoxelText variant="bread-normal">
                  Your team account is on the <span className="font-bold">{team.appTier.name}</span>{' '}
                  plan.
                </VoxelText>
              )}
              {team.appTier.tierType === AppTierTypeGq.NONE && (
                <VoxelText variant="bread-normal">
                  Your team account doesn't have a subscription plan
                </VoxelText>
              )}

              {team.appTier.tierType === AppTierTypeGq.SPONSORED && (
                <VoxelText variant="bread-normal">
                  Your sponsored subscription has been provided by{' '}
                  {team.sponsoredSubscriptionProvidedBy.map((t) => t.name).join(', ')}
                </VoxelText>
              )}
              {trialExpired && <VoxelText>Plan has expired.</VoxelText>}
              {trialExpires && !trialExpired && (
                <VoxelText>
                  {' '}
                  Expires {moment
                    .utc(team.activeSubscriptionPeriod?.publicPeriodEnd)
                    .fromNow()}{' '}
                  <VoxelText span variant="form-caption" noWrap>
                    (
                    {moment
                      .utc(team.activeSubscriptionPeriod?.publicPeriodEnd)
                      .format('yyyy-MM-DD')}
                    )
                  </VoxelText>
                  .
                  {team.eligibleForSponsoredSubscription && (
                    <>
                      Then you'll fallback to a sponsored plan provided by{' '}
                      {team.sponsoredSubscriptionProvidedBy.map((t) => t.name).join(', ')}.
                    </>
                  )}
                </VoxelText>
              )}
              {subscriptionStartsLater && (
                <VoxelText>
                  {' '}
                  trial expires{' '}
                  {moment.utc(team.activeSubscriptionPeriod?.publicPeriodEnd).fromNow()}{' '}
                  <VoxelText span variant="form-caption" noWrap>
                    (
                    {moment
                      .utc(team.activeSubscriptionPeriod?.publicPeriodEnd)
                      .format('yyyy-MM-DD')}
                    )
                  </VoxelText>
                  . Then your paid plan starts.
                </VoxelText>
              )}
            </div>

            {showPayment && (
              <div className="flex flex-row items-start gap-4">
                {/* Stripe subscription setup button */}
                {!team.stripeSubscriptionAvailable && (
                  <SetupSubscriptionButton team={team} user={user} />
                )}

                {/* Stripe portal button */}
                {!!team.stripeCustomerAvailable && (
                  <div className="my-6">
                    <VoxelButton
                      startIcon={<FontAwesomeIcon icon={faCreditCard} />}
                      color={'gray'}
                      size="xl"
                      title={
                        !!team.stripeSubscriptionAvailable
                          ? 'Manage billing and invoices'
                          : 'Manage invoices'
                      }
                      disabled={creatingPortalSession}
                      onClick={handleCreatePortalSession}
                    />
                  </div>
                )}
              </div>
            )}
          </VoxelPanel>

          <SubscriptionEvents subscriptionEvents={subscriptionEvents} />
        </div>

        {/* Capacity section */}

        {isPaid && (
          <div className="flex flex-col gap-6">
            {/* License settings section */}
            {isPaid && <LicenseSettings team={team} reload={reload} />}
          </div>
        )}
      </div>
    </>
  );
};

export default SubscriptionPage;
